
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const DustTransition = ({ isDusting, onComplete, themeColor }) => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    if (!isDusting) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Create particles based on screen content simulation
    const particleCount = 400; // Optimal for performance/visuals
    const particles = [];
    
    // Define colors based on the theme we are LEAVING
    // If leaving light theme (white background), dust should be whitish/gray
    // If leaving dark theme (black background), dust should be dark/purpleish
    const colors = themeColor === 'light' 
      ? ['#ffffff', '#f0f0f0', '#e0e0e0', '#d1d1d1', '#c0c0c0'] // Light debris
      : ['#1a1a1a', '#2d2d2d', '#404040', '#6c5ce7', '#8b7ff4']; // Dark debris with accent

    for (let i = 0; i < particleCount; i++) {
        const resetParticle = () => {
            return {
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                size: Math.random() * 4 + 1,
                speedX: (Math.random() - 0.5) * 4, // Horizontal drift
                speedY: (Math.random() - 1) * -4 - 1,   // Upward drift (fading away)
                color: colors[Math.floor(Math.random() * colors.length)],
                alpha: Math.random() * 0.5 + 0.5,
                rotation: Math.random() * 360,
                rotationSpeed: (Math.random() - 0.5) * 5
            };
        };
        particles.push(resetParticle());
    }

    let animationId;
    let startTime = Date.now();
    const duration = 2000; // 2 seconds total animation

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      if (progress >= 1) {
        if (animationId) cancelAnimationFrame(animationId);
        onComplete();
        return; 
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw background overlay that fades out
      // This hides the new theme initially and reveals it slowly
      const overlayColor = themeColor === 'light' ? '255, 255, 255' : '15, 15, 15';
      const overlayAlpha = Math.max(0, 1 - progress * 1.5); // Fades faster than particles
      
      ctx.fillStyle = `rgba(${overlayColor}, ${overlayAlpha})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach(p => {
        // Move particle
        p.x += p.speedX;
        p.y += p.speedY; 
        p.rotation += p.rotationSpeed;
        
        // Add some "wind" or noise
        p.x += Math.sin(p.y * 0.01 + elapsed * 0.002) * 0.5;

        // Update alpha
        const fadeStart = 0.2; 
        if (progress > fadeStart) {
           const fadeProgress = (progress - fadeStart) / (1 - fadeStart);
           p.alpha = Math.max(0, 1 - fadeProgress);
        }

        // Draw particle 
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color;
        
        ctx.beginPath();
        ctx.moveTo(0, -p.size);
        ctx.lineTo(p.size, p.size);
        ctx.lineTo(-p.size, p.size);
        ctx.closePath();
        ctx.fill();
        
        ctx.restore();
      });

      animationId = requestAnimationFrame(animate); 
    };

    animate();

    const handleResize = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
    }
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
    };
  }, [isDusting, onComplete, themeColor]);

  return (
    <AnimatePresence>
      {isDusting && (
        <motion.div
           initial={{ opacity: 1 }}
           animate={{ opacity: 1 }} // Canvas handles the fade
           exit={{ opacity: 0 }} // Smooth exit
           style={{
             position: 'fixed',
             top: 0,
             left: 0,
             width: '100%',
             height: '100%',
             zIndex: 99999, // On top of everything
             pointerEvents: 'none',
             mixBlendMode: 'normal'
           }}
        >
          <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DustTransition;
